import axios from 'axios';

import {
  ACCESS_TOKEN,
  API_END_POINT,
  API_URL,
  REFRESH_TOKEN,
  ROUTES,
} from '../constant/constants';
import {
  deleteCookie,
  getToken,
  setCookieWithExpiration,
} from '../utils/authUtils';

// Helper function to get access token from cookies
const getAccessToken = () => {
  return getToken(ACCESS_TOKEN);
};

// Helper function to get refresh token from cookies
const getRefreshToken = () => {
  return getToken(REFRESH_TOKEN);
};

// Helper function to refresh access token
const refreshAccessToken = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post(
      API_URL + API_END_POINT.REFRESH_TOKEN,
      {
        refreshToken: refreshToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Client-type': 'admin',
        },
        timeout: 10000,
      },
    );

    if (response.data?.hideErrorFromUser && response.data?.data) {
      const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
        response.data.data;

      // Update cookies
      setCookieWithExpiration(ACCESS_TOKEN, newAccessToken, 1); // 1 day
      setCookieWithExpiration(REFRESH_TOKEN, newRefreshToken, 7); // 7 days

      return newAccessToken;
    } else {
      throw new Error('Failed to refresh token');
    }
  } catch (error) {
    // If refresh fails, clear tokens and redirect to login
    deleteCookie(ACCESS_TOKEN);
    deleteCookie(REFRESH_TOKEN);
    window.location.href = ROUTES.LOGIN;
    throw error;
  }
};

// Helper function to get default headers with access token
const getDefaultHeaders = (customHeaders = {}, isFormData = false) => {
  const accessToken = getAccessToken();
  const headers = {
    ...customHeaders,
  };

  // Only set Content-Type to application/json if it's not FormData
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
};

// Helper function to make API call with token refresh logic
const makeApiCall = async (apiCall, retryCount = 0) => {
  try {
    return await apiCall();
  } catch (error) {
    // Check if it's a 401 error and we haven't retried yet
    if (error.response?.status === 401 && retryCount === 0) {
      try {
        // Try to refresh the token
        await refreshAccessToken();
        // Retry the original request with new token
        return await makeApiCall(apiCall, retryCount + 1);
      } catch (refreshError) {
        // If refresh fails, return the original error
        return error;
      }
    }
    return error;
  }
};

const axiosPostCall = async ({ apiEndPoint, body, header = {} }) => {
  const apiCall = async () => {
    const apiUrl = API_URL + apiEndPoint;
    let bodyData = body;
    const isFormData = body instanceof FormData;

    // Don't stringify if it's FormData
    if (!isFormData) {
      bodyData = JSON.stringify(body);
    }

    const response = await axios.post(apiUrl, bodyData, {
      headers: getDefaultHeaders(header, isFormData),
      timeout: 10000,
    });
    return response.data;
  };

  return await makeApiCall(apiCall);
};

const axiosGetCall = async ({ apiEndPoint, header = {} }) => {
  const apiCall = async () => {
    const apiUrl = API_URL + apiEndPoint;
    const response = await axios.get(apiUrl, {
      headers: getDefaultHeaders(header),
      timeout: 10000,
    });
    return response.data;
  };

  return await makeApiCall(apiCall);
};

const axiosDeleteCall = async ({ apiEndPoint, header = {} }) => {
  const apiCall = async () => {
    const apiUrl = API_URL + apiEndPoint;
    const response = await axios.delete(apiUrl, {
      headers: getDefaultHeaders(header),
      timeout: 10000,
    });
    return response.data;
  };

  return await makeApiCall(apiCall);
};

const axiosPutCall = async ({ apiEndPoint, body, header = {} }) => {
  const apiCall = async () => {
    const apiUrl = API_URL + apiEndPoint;
    let bodyData = JSON.stringify(body);
    const response = await axios.put(apiUrl, bodyData, {
      headers: getDefaultHeaders(header),
      timeout: 10000,
    });
    return response.data;
  };

  return await makeApiCall(apiCall);
};

export { axiosGetCall, axiosPostCall, axiosDeleteCall, axiosPutCall };
